import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Switch,
  Avatar,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createGroupChannel, updateGroupChannel, listChannelMembers, updateChannelMembers, deleteGroupChannel } from '../../services/internalChatService';
import { listUsersPublic } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

interface ChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  conversationId?: string | null; // Jeśli podane, to edycja istniejącego kanału
  initialTitle?: string;
}

type UserItem = { user_id: string; display_name?: string; email?: string; avatar_url?: string | null };

export default function ChannelDialog({ open, onClose, onSuccess, conversationId, initialTitle }: ChannelDialogProps) {
  const { user } = useAuth();
  const [title, setTitle] = React.useState(initialTitle || '');
  const [availableUsers, setAvailableUsers] = React.useState<UserItem[]>([]);
  const [memberIds, setMemberIds] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(false);
  const [loadingMembers, setLoadingMembers] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const isEditMode = Boolean(conversationId);

  // Załaduj wszystkich użytkowników
  React.useEffect(() => {
    if (!open) {
      setAvailableUsers([]);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const users = await listUsersPublic();
        if (mounted) {
          setAvailableUsers(users);
        }
      } catch (e) {
        console.error('Błąd ładowania użytkowników', e);
        if (mounted) setAvailableUsers([]);
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  // Załaduj członków kanału jeśli edytujemy
  React.useEffect(() => {
    if (!open || !conversationId || !isEditMode) {
      setMemberIds(new Set());
      return;
    }
    let mounted = true;
    setLoadingMembers(true);
    (async () => {
      try {
        const members = await listChannelMembers(conversationId);
        if (mounted) {
          setMemberIds(new Set(members.map(m => m.user_id)));
        }
      } catch (e) {
        console.error('Błąd ładowania członków', e);
      } finally {
        if (mounted) setLoadingMembers(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, conversationId, isEditMode]);

  // Resetuj formularz przy zamknięciu/otwarciu
  React.useEffect(() => {
    if (!open) {
      setTitle('');
      setMemberIds(new Set());
    } else {
      setTitle(initialTitle || '');
      if (!isEditMode && user?.user_id) {
        setMemberIds(new Set([user.user_id]));
      }
    }
  }, [open, initialTitle, isEditMode, user?.user_id]);

  const toggleMember = (userId: string) => {
    setMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        if (userId === user?.user_id) return prev;
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    const ids = Array.from(memberIds);
    if (!ids.includes(user?.user_id || '')) {
      ids.push(user?.user_id || '');
    }
    if (ids.length === 0) {
      alert('Kanał musi mieć przynajmniej jednego członka');
      return;
    }
    setLoading(true);
    try {
      if (isEditMode && conversationId) {
        await updateGroupChannel(conversationId, title.trim());
        await updateChannelMembers(conversationId, ids);
      } else {
        await createGroupChannel(title.trim(), ids);
      }
      onSuccess?.();
      onClose();
    } catch (e: any) {
      console.error('Błąd zapisywania kanału', e);
      alert(e?.response?.data?.message || 'Błąd zapisywania kanału');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!conversationId || !window.confirm(`Czy na pewno chcesz usunąć kanał „${title || initialTitle}”? Ta operacja jest nieodwracalna.`)) return;
    setDeleting(true);
    try {
      await deleteGroupChannel(conversationId);
      onSuccess?.();
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Błąd usuwania kanału');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          minWidth: 480,
          maxHeight: '90vh',
        },
      }}
      sx={{ zIndex: 2147483700 }}
    >
      <DialogTitle sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#020242', pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {isEditMode ? 'Edytuj kanał' : 'Utwórz nowy kanał'}
          <IconButton size="small" onClick={onClose} aria-label="Zamknij">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nazwa kanału"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            placeholder="np. Księgowość"
            disabled={loading}
            size="small"
            autoFocus={!isEditMode}
            inputProps={{ tabIndex: 0 }}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontFamily: 'Roboto, sans-serif' }}>
              Członkowie kanału
            </Typography>
            {loadingMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List
                dense
                sx={{
                  maxHeight: 320,
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  py: 0,
                }}
              >
                {availableUsers.map((u) => {
                  const isMe = u.user_id === user?.user_id;
                  const isMember = memberIds.has(u.user_id);
                  return (
                    <ListItem
                      key={u.user_id}
                      secondaryAction={
                        <Switch
                          edge="end"
                          checked={isMember}
                          onChange={() => toggleMember(u.user_id)}
                          disabled={loading || isMe}
                          size="small"
                        />
                      }
                      sx={{ py: 0.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar src={u.avatar_url || undefined} sx={{ width: 36, height: 36 }}>
                          {(u.display_name || u.email || '?').slice(0, 1).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif' }}>
                            {u.display_name || u.email || u.user_id}
                            {isMe && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                (Ty)
                              </Typography>
                            )}
                          </Typography>
                        }
                        secondary={u.display_name && u.email ? u.email : null}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontFamily: 'Roboto, sans-serif' }}>
              Ty zawsze będziesz członkiem kanału
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <Box>
          {isEditMode && (
            <Button
              onClick={handleDelete}
              disabled={loading || deleting}
              size="small"
              color="error"
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, textTransform: 'none' }}
            >
              {deleting ? <CircularProgress size={16} /> : 'Usuń'}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            size="small"
            sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !title.trim()}
            sx={{ backgroundColor: '#4040DB', '&:hover': { backgroundColor: '#1F1FB0' } }}
            size="small"
          >
            {loading ? <CircularProgress size={16} /> : (isEditMode ? 'Zapisz' : 'Utwórz')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

